import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {InputTextModule} from 'primeng/inputtext';
import {SelectModule} from 'primeng/select';

import {COUNTRY_OPTIONS, PhoneValue, formatNationalAsYouType} from '@shared/utils';

const DEFAULT_COUNTRY = 'UA';

/**
 * Reusable phone control: country select (round flag + dial code) + national number.
 * Implements ControlValueAccessor — bind via `formControlName` / `[formControl]`.
 * Value: `{ number, countryIso2 }`. Digits-only input with a live per-country mask
 * (libphonenumber AsYouType) and caret preservation. Country list is virtual-scrolled.
 */
@Component({
  selector: 'crm-phone-input',
  imports: [FormsModule, SelectModule, InputTextModule, IconFieldModule, InputIconModule],
  templateUrl: './phone-input.html',
  styleUrl: './phone-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneInput), multi: true}],
})
export class PhoneInput implements ControlValueAccessor {
  readonly placeholder = input('');
  readonly invalid = input(false);

  private readonly numberInput = viewChild<ElementRef<HTMLInputElement>>('numberInput');

  protected readonly countryOptions = COUNTRY_OPTIONS;
  protected readonly countryIso2 = signal(DEFAULT_COUNTRY);
  protected readonly number = signal('');
  protected readonly disabled = signal(false);

  private onChange: (value: PhoneValue) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor() {
    // Push the model into the native input only when they differ. As-you-type
    // reformatting sets the value (and caret) itself, so this stays a no-op then
    // and the caret is preserved; it only fires for programmatic writes (patch).
    effect(() => {
      const el = this.numberInput()?.nativeElement;
      const value = this.number();
      if (el && el.value !== value) {
        el.value = value;
      }
    });
  }

  writeValue(value: PhoneValue | null): void {
    const iso2 = value?.countryIso2 || DEFAULT_COUNTRY;
    this.countryIso2.set(iso2);
    this.number.set(formatNationalAsYouType((value?.number ?? '').replace(/\D/g, ''), iso2));
  }

  registerOnChange(fn: (value: PhoneValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onCountryChange(iso2: string): void {
    this.countryIso2.set(iso2);
    this.number.set(formatNationalAsYouType(this.number().replace(/\D/g, ''), iso2));
    this.emit();
  }

  protected onNumberInput(el: HTMLInputElement): void {
    const caret = el.selectionStart ?? el.value.length;
    const digitsBeforeCaret = this.countDigits(el.value.slice(0, caret));
    const formatted = formatNationalAsYouType(el.value.replace(/\D/g, ''), this.countryIso2());

    el.value = formatted;
    const newCaret = this.caretAfterDigits(formatted, digitsBeforeCaret);
    el.setSelectionRange(newCaret, newCaret);

    this.number.set(formatted);
    this.emit();
  }

  protected markTouched(): void {
    this.onTouched();
  }

  private emit(): void {
    this.onChange({number: this.number(), countryIso2: this.countryIso2()});
  }

  private countDigits(value: string): number {
    return (value.match(/\d/g) ?? []).length;
  }

  private caretAfterDigits(formatted: string, digitIndex: number): number {
    if (digitIndex <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < formatted.length; i += 1) {
      if (formatted[i] >= '0' && formatted[i] <= '9') {
        seen += 1;
        if (seen === digitIndex) return i + 1;
      }
    }
    return formatted.length;
  }
}
